import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  alpha,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle as CheckCircleIcon,
  ChevronLeft,
  ChevronRight,
  VideoLibrary as VideoPlaceholderIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { APP_URL } from '../../config';
import {
  isYouTubeUrl,
  getYouTubeId,
  getYouTubeThumbnail,
  playVideo,
  pauseAndResetVideo,
  stopAllVideos,
} from '../../utils/videoUtils';
import { htmlContentStyles } from '../../utils/stylesUtils';

const serverUrl = APP_URL;

// Styled components for module cards with video shorts design (9:16 aspect ratio)
const ModuleCard = styled(Box)(({ theme, isActive, isLocked, isSelected }) => ({
  position: 'relative',
  borderRadius: '12px', // rounded-xl equivalent
  overflow: 'hidden',
  cursor: isLocked ? 'not-allowed' : 'pointer',
  aspectRatio: '9/16',
  backgroundColor: theme.palette.background.paper,
  border: `2px solid ${
    isSelected
      ? theme.palette.primary.main
      : isActive
      ? theme.palette.primary.main
      : 'transparent'
  }`,
  boxShadow: isSelected
    ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`
    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // shadow-sm
  transition: 'all 0.3s ease',
  opacity: isLocked ? 0.6 : 1,
  '&:hover': {
    borderColor: isLocked ? 'transparent' : theme.palette.primary.main,
    '& .module-overlay': {
      opacity: 0,
    },
    '& .module-video': {
      opacity: 1,
      zIndex: 2,
      transform: !isSelected ? 'scale(1.05)' : 'scale(1)',
    },
    '& .module-thumbnail': {
      transform: !isSelected ? 'scale(1.05)' : 'scale(1)',
    },
  },
}));

const ModuleVideo = styled('video')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
  top: 0,
  left: 0,
  transition: 'opacity 0.3s ease, transform 0.3s ease',
  opacity: 0,
  zIndex: 2,
});

const ModuleThumbnail = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
  top: 0,
  left: 0,
  transition: 'transform 0.3s ease',
});

const ModuleOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(to top, ${alpha(
    theme.palette.common.black,
    0.8
  )} 0%, transparent 100%)`,
  transition: 'opacity 0.3s ease',
  opacity: 1,
  zIndex: 3,
  pointerEvents: 'none',
}));

const VideoOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  zIndex: 2,
});

const ModuleOverlayHover = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: alpha(theme.palette.common.black, 0.5),
  transition: 'opacity 0.3s ease',
  opacity: 1,
  zIndex: 3,
  pointerEvents: 'none',
}));

const CompletedBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 3,
  backgroundColor: alpha(theme.palette.success.main, 0.9),
  borderRadius: '50%',
  padding: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: theme.shadows[2],
}));

// Styled component for module card content
const ModuleCardContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: `linear-gradient(to top, ${alpha(
    theme.palette.common.black,
    0.95
  )} 0%, ${alpha(theme.palette.common.black, 0.8)} 60%, transparent 100%)`,
  color: theme.palette.common.white,
  padding: theme.spacing(2.5),
  zIndex: 4,
  pointerEvents: 'none',
  minHeight: '120px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
}));

const ModuleNumber = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem', // text-xs
  fontWeight: 600,
  color: alpha(theme.palette.common.white, 0.8),
  marginBottom: theme.spacing(0.5),
}));

const ModuleTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.125rem', // text-lg
  lineHeight: 1.3,
  marginBottom: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
}));

// Styled component for selected module info block
const SelectedModuleBlock = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '16px',
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow:
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  position: 'relative',
}));

const SelectedModuleTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.5rem', // text-xl
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const SelectedModuleDescription = styled(Box)(({ theme }) => ({
  fontSize: '1rem', // text-base
  lineHeight: 1.6,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
}));

const SelectedModuleContent = styled(Box)(({ theme }) => ({
  fontSize: '0.875rem', // text-sm
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
}));

const ModulesSection = ({
  modules,
  hasSpecialAccess,
  selectedModule,
  onModuleSelect,
}) => {
  const { t, i18n } = useTranslation(['courses', 'common']);
  const theme = useTheme();
  const [activeModule, setActiveModule] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [clickedModules, setClickedModules] = useState({});
  const [showYouTubeVideo, setShowYouTubeVideo] = useState({});
  const videoRefs = useRef({});

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('xl'));

  // Calculate modules per page based on screen size
  const calculateModulesPerPage = useCallback(() => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    if (isDesktop) return 3;
    return 2; // fallback
  }, [isMobile, isTablet, isDesktop]);

  const modulesPerPage = calculateModulesPerPage();

  // Helper function to pluralize module word
  const getModulePlural = (count) => {
    if (i18n.language === 'ru') {
      const cases = [2, 0, 1, 1, 1, 2];
      return ['модуль', 'модуля', 'модулей'][
        count % 100 > 4 && count % 100 < 20 ? 2 : cases[Math.min(count % 10, 5)]
      ];
    }
    return count === 1 ? 'Module' : 'Modules';
  };

  // Helper function to get module data from selectedModule
  const getSelectedModuleData = () => {
    return selectedModule?.module || selectedModule;
  };

  // Helper function to check if module is selected
  const isModuleSelected = (module) => {
    const selectedModuleData = getSelectedModuleData();
    return selectedModuleData && selectedModuleData._id === module._id;
  };

  // Stop video and reset all states when selection changes
  useEffect(() => {
    // Stop all videos using utility function
    stopAllVideos(videoRefs.current);

    // Reset all video states to default
    setActiveModule(null);
    setClickedModules({});
    setShowYouTubeVideo({});
  }, [selectedModule]);

  // Reset pagination when modules per page changes
  useEffect(() => {
    setCurrentPage(0);
  }, [modulesPerPage]);

  // Handle hover to autoplay video (only for non-selected and non-clicked modules)
  const handleMouseEnter = (moduleIndex, module) => {
    // Only play video if module is not selected and not clicked
    if (isModuleSelected(module) || clickedModules[moduleIndex]) {
      return;
    }

    setActiveModule(moduleIndex);
    const videoRef = videoRefs.current[moduleIndex];

    // Check if module has a video
    if (videoRef && module.videoUrl) {
      playVideo(videoRef);
    }
  };

  const handleMouseLeave = (moduleIndex) => {
    // Don't stop video if it was clicked
    if (clickedModules[moduleIndex]) {
      return;
    }

    setActiveModule(null);
    const videoRef = videoRefs.current[moduleIndex];

    if (videoRef) {
      pauseAndResetVideo(videoRef);
    }
  };

  const handleModuleClick = (module, actualIndex, isYouTube, hasVideo) => {
    if (module.locked && !hasSpecialAccess) {
      return;
    }

    // Check if this module is currently selected
    const isCurrentlySelected = isModuleSelected(module);

    // If module is already selected, play video instead of changing selection
    if (isCurrentlySelected) {
      if (isYouTube && !showYouTubeVideo[actualIndex]) {
        // Open YouTube iframe
        setShowYouTubeVideo((prev) => ({ ...prev, [actualIndex]: true }));
      } else if (hasVideo && !clickedModules[actualIndex]) {
        // Start regular video with controls
        const videoRef = videoRefs.current[actualIndex];
        if (videoRef) {
          setClickedModules((prev) => ({ ...prev, [actualIndex]: true }));
          videoRef.currentTime = 0;
          setTimeout(() => {
            playVideo(videoRef);
          }, 50);
        }
      }
      return;
    }

    // Module is not selected, so select it
    if (onModuleSelect) {
      onModuleSelect(module);
    }
  };

  // Get video URL or thumbnail
  const getMediaUrl = (module) => {
    if (module.videoUrl) {
      return module.videoUrl.startsWith('http')
        ? module.videoUrl
        : `${serverUrl}${module.videoUrl}`;
    }

    if (module.thumbnail) {
      return module.thumbnail.startsWith('http')
        ? module.thumbnail
        : `${serverUrl}${module.thumbnail}`;
    }

    // Fallback to course image if available
    return null;
  };

  // Check if module is completed
  const isModuleCompleted = (module) => {
    if (!module.lessons) return false;
    return module.lessons.every((lesson) => lesson.completed);
  };

  // Pagination logic - scroll by 1 module at a time
  const startIndex = currentPage;
  const endIndex = startIndex + modulesPerPage;
  const currentModules = modules.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) =>
      Math.min(modules.length - modulesPerPage, prev + 1)
    );
  };

  if (!modules || modules.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          {t('courses:courseCreate.ui.modulesAndLessons')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {modules.length} {getModulePlural(modules.length)}
          </Typography>
          <Box>
            <IconButton
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              sx={{
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={handleNextPage}
              disabled={currentPage >= modules.length - modulesPerPage}
              sx={{
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {currentModules.map((moduleRef, index) => {
          const module = moduleRef.module || moduleRef;
          const mediaUrl = getMediaUrl(module);
          const completed = isModuleCompleted(module);
          const isLocked = module.locked && !hasSpecialAccess;
          const isActive = activeModule === startIndex + index;
          const isSelected = isModuleSelected(module);
          const isYouTube = isYouTubeUrl(module.videoUrl);
          const youtubeThumbnail = isYouTube
            ? getYouTubeThumbnail(module.videoUrl)
            : null;

          const actualIndex = startIndex + index;

          // Responsive grid sizing
          const getGridSize = () => {
            if (isMobile) return { xs: 12 };
            if (isTablet) return { xs: 12, sm: 6 };
            if (isDesktop) return { xs: 12, sm: 6, md: 4 };
            return { xs: 12, sm: 6 };
          };

          return (
            <Grid item {...getGridSize()} key={actualIndex}>
              <ModuleCard
                isActive={isActive}
                isLocked={isLocked}
                isSelected={isSelected}
                onMouseEnter={() => handleMouseEnter(actualIndex, module)}
                onMouseLeave={() => handleMouseLeave(actualIndex)}
                onClick={() =>
                  handleModuleClick(
                    module,
                    actualIndex,
                    isYouTube,
                    mediaUrl && module.videoUrl
                  )
                }
              >
                {/* Preview Image (background layer) */}
                {module.previewImage && (
                  <ModuleThumbnail
                    src={
                      module.previewImage.startsWith('http')
                        ? module.previewImage
                        : `${serverUrl}${module.previewImage}`
                    }
                    alt={module.title}
                    style={{ zIndex: 1 }}
                  />
                )}

                {/* Video or Thumbnail (shown on hover or when no preview image) */}
                {isYouTube ? (
                  // YouTube video with iframe or thumbnail
                  showYouTubeVideo[actualIndex] ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${getYouTubeId(
                        module.videoUrl
                      )}?autoplay=1&controls=1`}
                      title={
                        module.name ||
                        module.title ||
                        t('courses:courseCreate.ui.module')
                      }
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        zIndex: 2,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : !module.previewImage ? (
                    <>
                      <ModuleThumbnail
                        className="module-thumbnail"
                        src={youtubeThumbnail}
                        alt={module.title}
                      />
                      <VideoOverlay />
                    </>
                  ) : null
                ) : mediaUrl && module.videoUrl ? (
                  // Regular video with autoplay (shows on hover if preview image exists)
                  <ModuleVideo
                    className="module-video"
                    ref={(el) => (videoRefs.current[actualIndex] = el)}
                    src={mediaUrl}
                    muted={!clickedModules[actualIndex]}
                    loop={isActive && !clickedModules[actualIndex]}
                    controls={clickedModules[actualIndex]}
                    playsInline
                    preload="metadata"
                    style={{
                      // Show video if clicked
                      opacity: clickedModules[actualIndex] ? 1 : undefined,
                      zIndex: clickedModules[actualIndex] ? 3 : undefined,
                    }}
                  />
                ) : !module.previewImage && mediaUrl ? (
                  <ModuleThumbnail src={mediaUrl} alt={module.title} />
                ) : !module.previewImage ? (
                  // Fallback placeholder (only if no preview image)
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.05)
                          : alpha(theme.palette.common.black, 0.04),
                      border: (theme) =>
                        `1px dashed ${alpha(theme.palette.text.primary, 0.2)}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      px: 2,
                      gap: 0.75,
                      color: (theme) => theme.palette.text.secondary,
                    }}
                  >
                    <VideoPlaceholderIcon
                      sx={{
                        fontSize: 40,
                        color: (theme) => theme.palette.text.disabled,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                    >
                      {t('courses:courseCreate.ui.module', 'Module')}{' '}
                      {actualIndex + 1}
                    </Typography>
                    <Typography variant="caption">
                      {t(
                        'courses:courseCreate.ui.noPreview',
                        'Preview not available'
                      )}
                    </Typography>
                  </Box>
                ) : null}

                {/* Hover Overlay - hide for selected modules */}
                {!isSelected && (
                  <ModuleOverlayHover className="module-overlay" />
                )}

                {/* Gradient Overlay - hide for selected modules */}
                {!isSelected && <ModuleOverlay />}

                {/* Completed Badge */}
                {completed && (
                  <CompletedBadge>
                    <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
                  </CompletedBadge>
                )}

                {/* Module Info - hide only when video is clicked and playing */}
                {!clickedModules[actualIndex] &&
                  !showYouTubeVideo[actualIndex] && (
                    <ModuleCardContent>
                      <ModuleNumber>
                        {t('courses:courseCreate.ui.module')} {actualIndex + 1}
                      </ModuleNumber>
                      <ModuleTitle>
                        {module.name ||
                          module.title ||
                          `${t('courses:courseCreate.ui.module')} ${
                            actualIndex + 1
                          }`}
                        {completed && (
                          <CheckCircleIcon
                            sx={{
                              ml: 1,
                              verticalAlign: 'middle',
                              fontSize: '1rem',
                              color: 'success.main',
                            }}
                          />
                        )}
                      </ModuleTitle>
                    </ModuleCardContent>
                  )}
              </ModuleCard>
            </Grid>
          );
        })}
      </Grid>

      {/* Selected Module Info Block */}
      {selectedModule &&
        (() => {
          const moduleData = getSelectedModuleData();
          const isCompleted = isModuleCompleted(moduleData);
          const moduleNumber =
            modules.findIndex((m) => {
              const mod = m.module || m;
              return mod._id === moduleData._id;
            }) + 1;

          return (
            <SelectedModuleBlock
              sx={{
                padding: { xs: 2, sm: 3, md: 4 },
                marginTop: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Chip
                label={`${t('courses:courseCreate.ui.module')} ${moduleNumber}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: 'primary.main',
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.1),
                  mb: 1,
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <SelectedModuleTitle
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  {moduleData.name || moduleData.title}
                </SelectedModuleTitle>
                {isCompleted && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon
                      sx={{ color: 'success.main', fontSize: 20 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: 'success.main', fontWeight: 600 }}
                    >
                      {t('courses:courseDetail.completed')}
                    </Typography>
                  </Box>
                )}
              </Box>

              {moduleData.description && (
                <SelectedModuleDescription
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    ...htmlContentStyles,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(moduleData.description),
                  }}
                />
              )}
              {moduleData.content && (
                <SelectedModuleContent
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    ...htmlContentStyles,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(moduleData.content),
                  }}
                />
              )}
            </SelectedModuleBlock>
          );
        })()}
    </Box>
  );
};

export default ModulesSection;
